import { memo, useCallback } from 'react';

import { WorkspaceAgentSelectChoiceBody } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/WorkspaceAgentSelectChoiceBody';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { useWorkspaceAgentElicitationTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentElicitationTracking';
import { useWorkspaceAgentSelectChoice } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentSelectChoice';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import {
  MULTI_SELECT_ELICITATION_TYPE,
  type MultiSelectElicitation,
  type SingleSelectElicitation,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type PrivateWorkspaceAgentPanelSelectDockProps = {
  elicitation: SingleSelectElicitation | MultiSelectElicitation;
};

export const PrivateWorkspaceAgentPanelSelectDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelSelectDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const options = elicitation.payload.options;
    const isMulti = elicitation.type === MULTI_SELECT_ELICITATION_TYPE;
    const trackChoice = useWorkspaceAgentElicitationTracking({
      createdAt: elicitation.createdAt,
      elicitationId: elicitation.id,
      elicitationType: isMulti ? 'multi_select' : 'single_select',
      optionCount: options.length,
    });
    const {
      canSubmit,
      choseCustom,
      customText,
      dismissCustom,
      handleOptionClick,
      selectedIndexes,
      setCustomText,
      trimmedCustomText,
    } = useWorkspaceAgentSelectChoice({ defaultIndexes: elicitation.payload.defaultIndexes, isMulti, options });

    const submit = useCallback(
      (indexes: number[], text?: string) => {
        trackChoice({ cta: 'approve', selectedIndexes: indexes, usedEscapeHatch: text !== undefined });
        void session.respondToElicitation({
          elicitationId: elicitation.id,
          response: 'approved',
          selectedIndexes: indexes,
          ...(text === undefined ? {} : { customText: text }),
        });
      },
      [elicitation.id, session, trackChoice],
    );

    const handleContinue = useCallback(() => {
      if (!canSubmit) {
        return;
      }

      submit(selectedIndexes, choseCustom ? trimmedCustomText : undefined);
    }, [canSubmit, choseCustom, selectedIndexes, submit, trimmedCustomText]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel="Continue"
        data-testid={isMulti ? 'WORKSPACE_AGENT_MULTI_SELECT' : 'WORKSPACE_AGENT_SINGLE_SELECT'}
        isApproveDisabled={!canSubmit}
        onApprove={handleContinue}
        onCancel={handleCancel}
        onPickOption={isMulti ? undefined : handleOptionClick}
        optionCount={options.length}
      >
        <WorkspaceAgentSelectChoiceBody
          customText={customText}
          isMulti={isMulti}
          onCustomDismiss={dismissCustom}
          onCustomTextChange={setCustomText}
          onOptionClick={handleOptionClick}
          options={options}
          otherLabel={isMulti ? undefined : elicitation.payload.otherLabel}
          selectedIndexes={selectedIndexes}
        />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelSelectDock.displayName = 'PrivateWorkspaceAgentPanelSelectDock';
